import React, { useState, useEffect } from 'react';
import { Users, Building2, Clock, CheckCircle, XCircle, ShieldCheck, LogOut, RefreshCw, Loader2 } from 'lucide-react';
import Badge from '../Ui/Badge';
import { AdminDashboardProps, PAGE_LIMIT } from '../types';
import { useLocation, useNavigate } from 'react-router-dom';
import Pagination from '../components/ui/Pagination';

const AdminDashboard = ({
    seekers,
    recruiters,
    updateSeekerStatus,
    updateRecruiterStatus,
    logout,
    refreshData,
    loading,
    seekerFilter,
    recruiterFilter,
    // 🔥 ADD THESE
    seekerPage,
    seekerTotalPages,
    seekerTotal,
    recruiterPage,
    recruiterTotalPages,
    recruiterTotal,
    onSeekerPageChange,
    onRecruiterPageChange,
    onSeekerFilter,
    onRecruiterFilter,
}: AdminDashboardProps & { refreshData: () => void; loading?: boolean }) => {

    const location = useLocation();
    const navigate = useNavigate();

    // Seeker details
    const [seekerDetailsOpen, setSeekerDetailsOpen] = useState(false);
    const [seekerDetailsLoading, setSeekerDetailsLoading] = useState(false);
    const [seekerDetailsData, setSeekerDetailsData] = useState<any>(null);

    // Recruiter details
    const [recruiterDetailsOpen, setRecruiterDetailsOpen] = useState(false);
    const [recruiterDetailsLoading, setRecruiterDetailsLoading] = useState(false);
    const [recruiterDetailsData, setRecruiterDetailsData] = useState<any>(null);
    const [boardStats, boardSetStats] = useState({
        seekers: {
            total: 0,
            submitted: 0,
            reviewed: 0,
            rejected: 0,
            hired: 0,
        },
        recruiters: {
            total: 0,
            pending: 0,
            approved: 0,
            blocked: 0,
        },
    });

    // Check current tab based on URL
    const isSeekers = location.pathname === '/admin/seekers';
    const isRecruiters = location.pathname === '/admin/recruiters';
    // Default to seekers if no specific tab in URL
    const [activeTab, setActiveTab] = useState<'seekers' | 'recruiters'>(
        isSeekers ? 'seekers' : (isRecruiters ? 'recruiters' : 'seekers')
    );

    const [refreshing, setRefreshing] = useState(false);
    const [stats, setStats] = useState({
        totalSeekers: 0,
        totalRecruiters: 0,
        pendingRecruiters: 0
    });

    // Calculate stats whenever seekers or recruiters change
    useEffect(() => {
        const pendingRecruiters = recruiters.filter(s => s.status === 'Pending').length;

        setStats({
            totalSeekers: seekers.length,
            totalRecruiters: recruiters.length,
            pendingRecruiters: pendingRecruiters
        });
    }, [seekers, recruiters]);

    // Update active tab when URL changes
    useEffect(() => {
        if (isSeekers) setActiveTab('seekers');
        if (isRecruiters) setActiveTab('recruiters');
    }, [isSeekers, isRecruiters]);

    useEffect(() => {
        fetchDashboardCounts();
    }, [seekerFilter, recruiterFilter]);

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            await refreshData();
            await fetchDashboardCounts();
        } finally {
            setRefreshing(false);
        }
    };

    const openSeekerDetails = async (id: number) => {
        setSeekerDetailsOpen(true);
        setSeekerDetailsLoading(true);
        setSeekerDetailsData(null);

        try {
            const res = await fetch(`https://wehireindia.com/wehire_api/seekers/${id}/`);
            const data = await res.json();
            setSeekerDetailsData(data);
        } catch (err) {
            console.error(err);
        } finally {
            setSeekerDetailsLoading(false);
        }
    };

    const openRecruiterDetails = async (id: number) => {
        setRecruiterDetailsOpen(true);
        setRecruiterDetailsLoading(true);
        setRecruiterDetailsData(null);

        try {
            const res = await fetch(`https://wehireindia.com/wehire_api/recruiters/${id}/`);
            const data = await res.json();
            setRecruiterDetailsData(data);
        } catch (err) {
            console.error(err);
        } finally {
            setRecruiterDetailsLoading(false);
        }
    };

    const fetchDashboardCounts = async () => {
        try {
            const res = await fetch(
                'https://wehireindia.com/wehire_api/auth/dashboard/counts/',
                { credentials: 'include' }
            );

            const data = await res.json();

            boardSetStats({
                seekers: {
                    total: data.seekers.total,
                    submitted: data.seekers.submitted,
                    reviewed: data.seekers.reviewed,
                    rejected: data.seekers.rejected,
                    hired: data.seekers.hired,
                },
                recruiters: {
                    total: data.recruiters.total,
                    pending: data.recruiters.pending,
                    approved: data.recruiters.approved,
                    blocked: data.recruiters.blocked,
                },
            });
        } catch (err) {
            console.error('Dashboard counts error:', err);
        }
    };

    const handleSeekerStatusUpdate = async (id: number, status: string) => {
        try {
            await updateSeekerStatus(id, status);
            await fetchDashboardCounts();
        } catch (error) {
            console.error('Error updating seeker status:', error);
        }
    };

    const handleRecruiterStatusUpdate = async (id: number, status: string) => {
        try {
            await updateRecruiterStatus(id, status);
            await fetchDashboardCounts();
        } catch (error) {
            console.error('Error updating recruiter status:', error);
        }
    };

    const handleTabClick = (tab: 'seekers' | 'recruiters') => {
        setActiveTab(tab);
        navigate(`/admin/${tab}`);
    };

    const statCards = [
        {
            label: "Total Seekers",
            val: stats.totalSeekers,
            icon: Users,
            color: "text-violet-600 dark:text-violet-400",
            bg: "bg-violet-50 dark:bg-violet-900/20"
        },
        {
            label: "Recruiters",
            val: stats.totalRecruiters,
            icon: Building2,
            color: "text-fuchsia-600 dark:text-fuchsia-400",
            bg: "bg-fuchsia-50 dark:bg-fuchsia-900/20"
        },
        {
            label: "Pending Applications",
            val: stats.pendingRecruiters,
            icon: Clock,
            color: "text-amber-600 dark:text-amber-400",
            bg: "bg-amber-50 dark:bg-amber-900/20"
        },
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-zinc-50 dark:bg-black flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-violet-500 mx-auto mb-4" />
                    <p className="text-zinc-600 dark:text-zinc-300">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-zinc-50 dark:bg-black flex flex-col">
            {/* Admin Header */}
            <header className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-md border-b border-zinc-200 dark:border-zinc-800 px-8 py-4 flex justify-between items-center sticky top-0 z-40">
                <div className="flex items-center gap-3">
                    <div className="bg-zinc-900 dark:bg-white text-white dark:text-black p-1.5 rounded-lg">
                        <ShieldCheck className="w-5 h-5" />
                    </div>
                    <h1 className="font-bold text-xl tracking-tight text-zinc-900 dark:text-white">Admin<span className="text-zinc-400 font-normal">Panel</span></h1>
                </div>
                <div className="flex items-center gap-4">
                    <button
                        onClick={handleRefresh}
                        disabled={refreshing}
                        className="flex items-center gap-2 text-zinc-500 hover:text-violet-600 dark:hover:text-violet-400 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                        {refreshing ? 'Refreshing...' : 'Refresh Data'}
                    </button>
                    <button
                        onClick={logout}
                        className="flex items-center gap-2 text-zinc-500 hover:text-red-500 transition-colors text-sm font-medium"
                    >
                        <LogOut className="w-4 h-4" /> Sign Out
                    </button>
                </div>
            </header>

            {/* Admin Content */}
            <main className="flex-1 p-8 max-w-7xl mx-auto w-full">

                <div className="lg:hidden bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-400 p-4 mb-6 rounded-r-lg">
                    <p className="text-amber-700 dark:text-amber-300 font-medium text-sm">⚠️ Optimization Warning: Please use desktop for full admin features.</p>
                </div>

                {/* Dynamic Stats Section */}
                {activeTab === 'seekers' && (
                    <div className="mb-10">
                        <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 dark:text-zinc-400 mb-5 ml-1">
                            Seekers Analytics
                        </h3>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                            {[
                                {
                                    label: 'Total',
                                    val: boardStats.seekers.total,
                                    color: 'text-violet-600 dark:text-violet-400',
                                    bg: 'bg-violet-50 dark:bg-violet-900/30',
                                    path: "M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
                                },
                                {
                                    label: 'Submitted',
                                    val: boardStats.seekers.submitted,
                                    color: 'text-blue-600 dark:text-blue-400',
                                    bg: 'bg-blue-50 dark:bg-blue-900/30',
                                    path: "M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z"
                                },
                                {
                                    label: 'Reviewed',
                                    val: boardStats.seekers.reviewed,
                                    color: 'text-amber-600 dark:text-amber-400',
                                    bg: 'bg-amber-50 dark:bg-amber-900/30',
                                    path: "M10.125 2.25h-4.5c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125v-9M10.125 2.25h.375a9 9 0 019 9v.375M10.125 2.25A3.375 3.375 0 0113.5 5.625v1.5c0 .621.504 1.125 1.125 1.125h1.5a3.375 3.375 0 013.375 3.375M9 15l2.25 2.25L15 12"
                                },
                                {
                                    label: 'Rejected',
                                    val: boardStats.seekers.rejected,
                                    color: 'text-red-600 dark:text-red-400',
                                    bg: 'bg-red-50 dark:bg-red-900/30',
                                    path: "M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                                },
                                {
                                    label: 'Hired',
                                    val: boardStats.seekers.hired,
                                    color: 'text-emerald-600 dark:text-emerald-400',
                                    bg: 'bg-emerald-50 dark:bg-emerald-900/30',
                                    path: "M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
                                },
                            ].map((item, i) => (
                                <div
                                    key={i}
                                    onClick={() => onSeekerFilter(item.label)}
                                    className={`cursor-pointer flex items-center gap-3 p-3 rounded-xl border transition-all
    ${seekerFilter === item.label
                                            ? "border-violet-50"
                                            : "border-zinc-200 dark:border-zinc-800"
                                        }
  `}
                                >
                                    {/* SVG Icon Section */}
                                    <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${item.bg} ${item.color}`}>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d={item.path} />
                                        </svg>
                                    </div>

                                    {/* Value & Label Section (Row) */}
                                    <div className="flex flex-1 flex-col justify-center min-w-0">
                                        <div className="flex items-baseline justify-between gap-2">
                                            <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 truncate">
                                                {item.label}
                                            </span>
                                            <span className={`text-lg font-black ${item.color}`}>
                                                {item.val}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'recruiters' && (
                    <div className="mb-10">
                        <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-400 mb-5 ml-1">
                            Recruiters Overview
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                            {[
                                {
                                    label: 'Total',
                                    val: boardStats.recruiters.total,
                                    color: 'text-fuchsia-600 dark:text-fuchsia-400',
                                    bg: 'bg-fuchsia-50 dark:bg-fuchsia-500/10',
                                    path: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                },
                                {
                                    label: 'Pending',
                                    val: boardStats.recruiters.pending,
                                    color: 'text-amber-600 dark:text-amber-400',
                                    bg: 'bg-amber-50 dark:bg-amber-500/10',
                                    path: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                },
                                {
                                    label: 'Approved',
                                    val: boardStats.recruiters.approved,
                                    color: 'text-emerald-600 dark:text-emerald-400',
                                    bg: 'bg-emerald-50 dark:bg-emerald-500/10',
                                    path: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                                },
                                {
                                    label: 'Blocked',
                                    val: boardStats.recruiters.blocked,
                                    color: 'text-red-600 dark:text-red-400',
                                    bg: 'bg-red-50 dark:bg-red-500/10',
                                    path: "M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                                },
                            ].map((item, i) => (
                                <div
                                    key={i}
                                    onClick={() => onRecruiterFilter(item.label)}
                                    className={`cursor-pointer flex items-center gap-3 p-3 rounded-xl border transition-all
    ${recruiterFilter === item.label
                                            ? "border-violet-50"
                                            : "border-zinc-200 dark:border-zinc-800"
                                        }
  `}
                                >

                                    {/* Icon Section */}
                                    <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${item.bg} ${item.color}`}>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                                            <path strokeLinecap="round" strokeLinejoin="round" d={item.path} />
                                        </svg>
                                    </div>

                                    {/* Content Section (Same Row) */}
                                    <div className="flex flex-1 items-center justify-between pr-2">
                                        <span className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
                                            {item.label}
                                        </span>
                                        <span className={`text-xl font-bold tracking-tight ${item.color}`}>
                                            {item.val}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {/* Management Area */}
                <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm border border-zinc-200 dark:border-zinc-800 overflow-hidden min-h-[600px]">
                    <div className="border-b border-zinc-200 dark:border-zinc-800 flex">
                        <button
                            onClick={() => handleTabClick('seekers')}
                            className={`px-8 py-5 font-bold text-sm transition-all relative ${activeTab === 'seekers'
                                ? 'text-violet-600 dark:text-violet-400 bg-violet-50/50 dark:bg-violet-900/10'
                                : 'text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                                }`}
                        >
                            Seekers Database
                            {activeTab === 'seekers' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-violet-600 dark:bg-violet-400"></div>}
                        </button>
                        <button
                            onClick={() => handleTabClick('recruiters')}
                            className={`px-8 py-5 font-bold text-sm transition-all relative ${activeTab === 'recruiters'
                                ? 'text-violet-600 dark:text-violet-400 bg-violet-50/50 dark:bg-violet-900/10'
                                : 'text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                                }`}
                        >
                            Recruiter Database
                            {activeTab === 'recruiters' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-violet-600 dark:bg-violet-400"></div>}
                        </button>
                    </div>

                    <div className="p-0 overflow-x-auto flex flex-col min-h-[600px]">
                        {activeTab === 'seekers' ? (
                            <>
                                {seekers.length === 0 ? (
                                    <div className="text-center py-20">
                                        <p className="text-zinc-400 dark:text-zinc-500">No seekers found.</p>
                                    </div>
                                ) : (
                                    <table className="w-full min-h-[calc(100vh-400px)] text-left border-collapse">
                                        <thead className="bg-zinc-50 dark:bg-zinc-800/50">
                                            <tr className="text-zinc-400 text-xs font-bold uppercase tracking-wider">
                                                <th className="py-4 px-6">Candidate</th>
                                                <th className="py-4 px-6">Role</th>
                                                <th className="py-4 px-6">Experience</th>
                                                <th className="py-4 px-6">Status</th>
                                                <th className="py-4 px-6">Phone Number</th>
                                                <th className="py-4 px-6 text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-sm">
                                            {seekers.map((s) => (
                                                <tr key={s.id} className="border-b border-zinc-100 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800/30 transition-colors">
                                                    <td className="py-2 px-6">
                                                        <div className="font-bold text-zinc-900 dark:text-white">{s.name}</div>
                                                        <div className="text-zinc-400 text-xs">{s.email}</div>
                                                    </td>
                                                    <td className="py-2 px-6 text-zinc-600 dark:text-zinc-300">{s.role}</td>
                                                    <td className="py-2 px-6 text-zinc-600 dark:text-zinc-300">{s.exp}</td>
                                                    <td className="py-2 px-6"><Badge status={s.status} /></td>
                                                    <td className="py-2 px-6 text-zinc-600 dark:text-zinc-300">{s.phone}</td>
                                                    <td className="py-2 px-6 text-center">
                                                        <div className="flex justify-center gap-2">
                                                            {/* details button i need here */}
                                                            <button
                                                                onClick={() => openSeekerDetails(s.id)}
                                                                className="p-2 rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                                                            >
                                                                👁
                                                            </button>

                                                            <button
                                                                onClick={() => s.status !== 'Reviewed' && handleSeekerStatusUpdate(s.id, 'Reviewed')}
                                                                disabled={s.status === 'Reviewed'}
                                                                className={`p-2 rounded-lg transition-colors ${s.status === 'Reviewed'
                                                                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                                                    : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/40'}`}
                                                                title={s.status === 'Reviewed' ? 'Already Reviewed' : 'Mark Reviewed'}
                                                            >
                                                                <CheckCircle className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => s.status !== 'Rejected' && handleSeekerStatusUpdate(s.id, 'Rejected')}
                                                                disabled={s.status === 'Rejected'}
                                                                className={`p-2 rounded-lg transition-colors ${s.status === 'Rejected'
                                                                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                                                    : 'bg-red-50 dark:bg-red-900/20 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/40'}`}
                                                                title={s.status === 'Rejected' ? 'Already Rejected' : 'Reject'}
                                                            >
                                                                <XCircle className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleSeekerStatusUpdate(s.id, 'Hired')}
                                                                disabled={s.status === 'Hired'}
                                                                className={`p-2 rounded-lg transition-colors ${s.status === 'Hired'
                                                                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                                                    : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 hover:bg-emerald-100 dark:hover:bg-emerald-900/40'}`}
                                                                title={s.status === 'Hired' ? 'Already Hired' : 'Mark Hired'}
                                                            >
                                                                <svg className='h-4 w-4' height="200px" width="200px" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 512 512" xmlSpace="preserve" fill="currentColor">
                                                                    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                                                                    <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                                                                    <g id="SVGRepo_iconCarrier">
                                                                        <path style={{ fill: "currentColor", opacity: s.status === 'Hired' ? "0.4" : "1" }} d="M290.221,456.964c-14.872-8.103-24.126-23.685-24.126-40.622V285.623 c8.349,3.521,17.523,5.47,27.153,5.47c19.592,0,37.292-8.063,49.983-21.048c-18.339-25.213-48.055-41.618-81.618-41.618h-25.059 h-60.478h-25.059c-55.714,0-100.88,45.165-100.88,100.88v167.85h312.353v-0.81L290.221,456.964z"></path>
                                                                        <path style={{ fill: "currentColor", opacity: s.status === 'Hired' ? "0.4" : "1" }} d="M461.862,270.781c-8.198,0-14.843,6.645-14.843,14.843v130.719c0,11.509-6.279,22.079-16.386,27.588 l-66.655,36.32l-66.654-36.32c-10.107-5.507-16.386-16.078-16.386-27.588V305.053c4.051,0.589,8.158,0.885,12.31,0.885 c1.259,0,2.513-0.039,3.763-0.094c0.328-0.015,0.658-0.028,0.984-0.046c1.128-0.062,2.252-0.142,3.369-0.249 c0.534-0.05,1.064-0.117,1.596-0.177c0.549-0.064,1.098-0.131,1.645-0.205c15.087-2.005,29.195-7.996,41.132-17.463 c0.364-0.286,0.727-0.573,1.087-0.867c0.429-0.352,0.856-0.705,1.279-1.066c0.855-0.726,1.697-1.467,2.523-2.226 c0.138-0.126,0.272-0.257,0.408-0.384c0.948-0.883,1.878-1.787,2.786-2.713c0.009-0.009,0.016-0.016,0.025-0.025 c0.001-0.001,0.004-0.004,0.006-0.006c3.743-3.827,7.146-8.021,10.138-12.552c7.169,10.807,16.809,19.988,28.021,26.541 c7.077,4.137,16.169,1.752,20.304-5.326c4.137-7.077,1.752-16.169-5.326-20.304c-9.976-5.83-18.035-14.851-22.691-25.401 c-2.373-5.379-7.699-8.85-13.579-8.85h-13.485c-5.879,0-11.205,3.47-13.579,8.85c-0.445,1.011-0.94,1.989-1.44,2.963 c-12.869-12.47-28.449-21.765-45.388-27.206c16.754-15.949,27.227-38.438,27.227-63.341V87.501c0-8.198-6.645-14.843-14.843-14.843 s-14.843,6.645-14.843,14.843v68.287c0,31.63-25.535,57.395-57.071,57.795h-61.963c-31.536-0.401-57.071-26.166-57.071-57.795 V87.501c0-31.879,25.936-57.815,57.815-57.815h60.478c8.198,0,14.843-6.645,14.843-14.843S244.753,0,236.555,0h-60.478 c-48.248,0-87.501,39.253-87.501,87.501v68.287c0,24.883,10.459,47.358,27.19,63.305c-46.621,14.947-80.47,58.693-80.47,110.213 v167.85c0,8.198,6.645,14.843,14.843,14.843h312.353c0.278,0,0.549-0.027,0.824-0.042c0.221,0.01,0.441,0.042,0.662,0.042 c2.443,0,4.888-0.603,7.103-1.809l73.757-40.191c19.657-10.711,31.867-31.27,31.867-53.654V285.626 C476.706,277.426,470.06,270.781,461.862,270.781z M64.984,482.313V329.307c0-47.441,38.596-86.036,86.036-86.036h24.315 c0.248,0.001,0.494,0.019,0.742,0.019h15.395v144.167c0,8.198,6.645,14.843,14.843,14.843s14.843-6.645,14.843-14.843V243.29h15.395 c0.249,0,0.494-0.016,0.742-0.019h24.315c22.688,0,44.419,9.089,60.403,24.818c-6.228,3.821-13.205,6.435-20.58,7.545 c-0.049,0.007-0.096,0.018-0.147,0.025c-1.149,0.169-2.31,0.288-3.475,0.384c-0.189,0.015-0.374,0.045-0.563,0.058 c-1.238,0.091-2.485,0.132-3.738,0.138c-0.089,0-0.178,0.009-0.267,0.009c-7.422,0-14.616-1.447-21.383-4.302 c-4.582-1.934-9.829-1.443-13.976,1.308c-4.144,2.75-6.636,7.393-6.636,12.369v130.719c0,6.996,1.192,13.812,3.432,20.206 c4.927,14.067,14.921,26.086,28.435,33.449l22.599,12.314H64.984V482.313z"></path>
                                                                        <polyline style={{ fill: "currentColor", opacity: s.status === 'Hired' ? "0.4" : "1" }} points="337.64,377.225 362.494,391.08 404.435,353.972 "></polyline>
                                                                        <path style={{ fill: "currentColor", opacity: s.status === 'Hired' ? "0.4" : "1" }} d="M362.489,405.924c-2.474,0-4.961-0.617-7.223-1.878l-24.854-13.853 c-7.16-3.991-9.73-13.032-5.738-20.193c3.991-7.162,13.031-9.731,20.193-5.738l15.669,8.734l34.064-30.139 c6.138-5.434,15.52-4.86,20.953,1.281c5.433,6.139,4.858,15.52-1.281,20.953l-41.941,37.108 C369.55,404.656,366.035,405.924,362.489,405.924z"></path>
                                                                    </g>
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                                <div className="mt-auto border-t border-zinc-200 dark:border-zinc-800">
                                    <Pagination
                                        page={seekerPage}
                                        limit={PAGE_LIMIT}
                                        total={seekerTotal}
                                        totalPages={seekerTotalPages}
                                        onChange={onSeekerPageChange}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                {recruiters.length === 0 ? (
                                    <div className="text-center py-20">
                                        <p className="text-zinc-400 dark:text-zinc-500">No recruiters found.</p>
                                    </div>
                                ) : (
                                    <table className="w-full text-left border-collapse">
                                        <thead className="bg-zinc-50 dark:bg-zinc-800/50">
                                            <tr className="text-zinc-400 text-xs font-bold uppercase tracking-wider">
                                                <th className="py-4 px-6">Company</th>
                                                <th className="py-4 px-6">Recruiter</th>
                                                <th className="py-4 px-6">Looking For</th>
                                                <th className="py-4 px-6">Status</th>
                                                <th className="py-4 px-6 text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-sm">
                                            {recruiters.map((r) => (
                                                <tr key={r.id} className="border-b border-zinc-100 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800/30 transition-colors">
                                                    <td className="py-4 px-6 font-bold text-zinc-900 dark:text-white">{r.company}</td>
                                                    <td className="py-4 px-6">
                                                        <div className="text-zinc-900 dark:text-zinc-200">{r.recruiter}</div>
                                                        <div className="text-zinc-400 text-xs">{r.email}</div>
                                                    </td>
                                                    <td className="py-4 px-6 text-zinc-600 dark:text-zinc-300">{r.role}</td>
                                                    <td className="py-4 px-6"><Badge status={r.status} /></td>
                                                    <td className="py-4 px-6 text-right">
                                                        <div className="flex justify-end gap-2">
                                                            {/* details button i need here */}
                                                            <button
                                                                onClick={() => openRecruiterDetails(r.id)}
                                                                className="px-3 py-1 rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-xs font-bold transition-colors"
                                                            >
                                                                Details
                                                            </button>
                                                            {r.status !== 'Approved' && (
                                                                <button
                                                                    onClick={() => handleRecruiterStatusUpdate(r.id, 'Approved')}
                                                                    className="px-3 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 text-xs font-bold border border-emerald-200 dark:border-emerald-800 transition-colors"
                                                                >
                                                                    Approve
                                                                </button>
                                                            )}
                                                            {r.status !== 'Blocked' && (
                                                                <button
                                                                    onClick={() => handleRecruiterStatusUpdate(r.id, 'Blocked')}
                                                                    className="px-3 py-1 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/40 text-xs font-bold border border-red-200 dark:border-red-800 transition-colors"
                                                                >
                                                                    Block
                                                                </button>
                                                            )}
                                                            {r.status === 'Blocked' && (
                                                                <button
                                                                    onClick={() => handleRecruiterStatusUpdate(r.id, 'Pending')}
                                                                    className="px-3 py-1 rounded-lg bg-gray-50 dark:bg-gray-900/20 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-900/40 text-xs font-bold border border-gray-300 dark:border-gray-700 transition-colors"
                                                                >
                                                                    Unblock
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                                <div className="mt-auto border-t border-zinc-200 dark:border-zinc-800">
                                    <Pagination
                                        page={recruiterPage}
                                        limit={PAGE_LIMIT}
                                        total={recruiterTotal}
                                        totalPages={recruiterTotalPages}
                                        onChange={onRecruiterPageChange}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                    {seekerDetailsOpen && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                            <div className="bg-white dark:bg-zinc-900 rounded-2xl w-full max-w-2xl shadow-xl border border-zinc-200 dark:border-zinc-800">
                                <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
                                    <h2 className="font-bold text-lg text-zinc-900 dark:text-white">Seeker Details</h2>
                                    <button onClick={() => setSeekerDetailsOpen(false)} className="text-zinc-400 hover:text-red-500 text-xl">✕</button>
                                </div>
                                <div className="p-6 max-h-[70vh] overflow-y-auto text-sm space-y-4">
                                    {seekerDetailsLoading ? (
                                        <div className="flex items-center justify-center py-10">
                                            <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
                                        </div>
                                    ) : seekerDetailsData ? (
                                        <div className="grid grid-cols-1 gap-4 p-6 max-h-[70vh] overflow-y-auto text-sm">
                                            <div className="flex justify-between">
                                                <span className="font-semibold text-zinc-700 dark:text-zinc-300">Name:</span>
                                                <span className="text-zinc-900 dark:text-zinc-100">{seekerDetailsData.name}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="font-semibold text-zinc-700 dark:text-zinc-300">Email:</span>
                                                <span className="text-zinc-900 dark:text-zinc-100">{seekerDetailsData.email}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="font-semibold text-zinc-700 dark:text-zinc-300">Phone:</span>
                                                <span className="text-zinc-900 dark:text-zinc-100">{seekerDetailsData.phone}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="font-semibold text-zinc-700 dark:text-zinc-300">Role:</span>
                                                <span className="text-zinc-900 dark:text-zinc-100">{seekerDetailsData.role}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="font-semibold text-zinc-700 dark:text-zinc-300">Experience:</span>
                                                <span className="text-zinc-900 dark:text-zinc-100">{seekerDetailsData.exp}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="font-semibold text-zinc-700 dark:text-zinc-300">Location:</span>
                                                <span className="text-zinc-900 dark:text-zinc-100">{seekerDetailsData.location}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="font-semibold text-zinc-700 dark:text-zinc-300">Status:</span>
                                                <Badge status={seekerDetailsData.status} />
                                            </div>
                                            <div className="flex justify-between gap-1">
                                                <span className="font-semibold text-zinc-700 dark:text-zinc-300">Resume:</span>
                                                {seekerDetailsData.resumeUrl ? (
                                                    <button
                                                        onClick={() => {
                                                            // Create a blob from the base64 data
                                                            const byteCharacters = atob(seekerDetailsData.resumeUrl.split(',')[1]);
                                                            const byteNumbers = new Array(byteCharacters.length);
                                                            for (let i = 0; i < byteCharacters.length; i++) {
                                                                byteNumbers[i] = byteCharacters.charCodeAt(i);
                                                            }
                                                            const byteArray = new Uint8Array(byteNumbers);
                                                            const blob = new Blob([byteArray], { type: 'application/pdf' });

                                                            // Create a blob URL and open it
                                                            const blobUrl = URL.createObjectURL(blob);
                                                            window.open(blobUrl, '_blank');

                                                            // Clean up the blob URL after a delay
                                                            setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
                                                        }}
                                                        className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer"
                                                    >
                                                        View Resume
                                                    </button>
                                                ) : (
                                                    <span className="text-zinc-500 dark:text-zinc-400">No resume uploaded</span>
                                                )}
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="font-semibold text-zinc-700 dark:text-zinc-300">Created At:</span>
                                                <span className="text-zinc-900 dark:text-zinc-100">{new Date(seekerDetailsData.createdAt).toLocaleString()}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="font-semibold text-zinc-700 dark:text-zinc-300">Updated At:</span>
                                                <span className="text-zinc-900 dark:text-zinc-100">{new Date(seekerDetailsData.updatedAt).toLocaleString()}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-zinc-500 dark:text-zinc-400 text-center py-10">No details found.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {recruiterDetailsOpen && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                            <div className="bg-white dark:bg-zinc-900 rounded-2xl w-full max-w-lg shadow-xl border border-zinc-200 dark:border-zinc-800">
                                {/* Header */}
                                <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
                                    <h2 className="font-bold text-lg text-zinc-900 dark:text-white">Recruiter Details</h2>
                                    <button
                                        onClick={() => setRecruiterDetailsOpen(false)}
                                        className="text-zinc-400 hover:text-red-500 text-xl"
                                    >
                                        ✕
                                    </button>
                                </div>

                                {/* Body */}
                                <div className="p-6 max-h-[70vh] overflow-y-auto text-sm space-y-4">
                                    {recruiterDetailsLoading ? (
                                        <div className="flex items-center justify-center py-10">
                                            <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
                                        </div>
                                    ) : recruiterDetailsData ? (
                                        <div className="grid grid-cols-1 gap-4">
                                            <div className="flex justify-between">
                                                <span className="font-semibold text-zinc-700 dark:text-zinc-300">Company:</span>
                                                <span className="text-zinc-900 dark:text-zinc-100">{recruiterDetailsData.company}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="font-semibold text-zinc-700 dark:text-zinc-300">Recruiter:</span>
                                                <span className="text-zinc-900 dark:text-zinc-100">{recruiterDetailsData.recruiter}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="font-semibold text-zinc-700 dark:text-zinc-300">Email:</span>
                                                <span className="text-zinc-900 dark:text-zinc-100">{recruiterDetailsData.email}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="font-semibold text-zinc-700 dark:text-zinc-300">Looking For:</span>
                                                <span className="text-zinc-900 dark:text-zinc-100">{recruiterDetailsData.role}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="font-semibold text-zinc-700 dark:text-zinc-300">Skills:</span>
                                                <span className="text-zinc-900 dark:text-zinc-100">{recruiterDetailsData.skills}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="font-semibold text-zinc-700 dark:text-zinc-300">Location:</span>
                                                <span className="text-zinc-900 dark:text-zinc-100">{recruiterDetailsData.location}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="font-semibold text-zinc-700 dark:text-zinc-300">Status:</span>
                                                <Badge status={recruiterDetailsData.status} />
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="font-semibold text-zinc-700 dark:text-zinc-300">Created At:</span>
                                                <span className="text-zinc-900 dark:text-zinc-100">
                                                    {new Date(recruiterDetailsData.createdAt).toLocaleString()}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="font-semibold text-zinc-700 dark:text-zinc-300">Updated At:</span>
                                                <span className="text-zinc-900 dark:text-zinc-100">
                                                    {new Date(recruiterDetailsData.updatedAt).toLocaleString()}
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-zinc-500 dark:text-zinc-400 text-center py-10">No details found.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default AdminDashboard;